import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon } from '@/components/layout/icons';
import { Input } from '@/components/ui/input';
import { useAllServices } from '@shared/hooks/use-services';
import { useMe } from '@shared/hooks/use-me';
import { fullName } from '@/lib/format';
import {
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  type ServiceCategory,
  type ServiceSummary,
} from '@shared/types/api';
import { ServiceCard } from './service-card';
import { cn } from '@/lib/cn';

type BrowseMode = 'CATEGORY' | 'AUTHORITY';

export const CATEGORY_IMAGES: Record<string, string> = {
  DOCUMENTATION: '/categories/01_documentation_4K.png',
  BUSINESS_REGISTRATION: '/categories/02_business_registration_4K.png',
  COMPLIANCE: '/categories/03_compliance_4K.png',
  FINANCE: '/categories/04_finance_4K.png',
  LEGAL: '/categories/05_legal_4K.png',
};

export function ServicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: me } = useMe();
  const firstName = fullName(me?.firstName ?? null, null).split(' ')[0];
  const { data: services = [], isLoading } = useAllServices();

  const query = searchParams.get('q') ?? '';
  const selectedCategory = searchParams.get('category');
  const selectedAuthorityId = searchParams.get('authority');
  const browseModeParam = searchParams.get('mode') as BrowseMode | null;
  const initialBrowseMode: BrowseMode =
    browseModeParam || (selectedCategory ? 'CATEGORY' : 'AUTHORITY');
  const [browseMode, setBrowseModeState] = useState<BrowseMode>(initialBrowseMode);

  useEffect(() => {
    if (browseModeParam && browseModeParam !== browseMode) {
      setBrowseModeState(browseModeParam);
    }
  }, [browseModeParam]);

  const setQuery = (q: string) => {
    const next = new URLSearchParams(searchParams);
    if (q.trim()) {
      next.set('q', q);
    } else {
      next.delete('q');
    }
    setSearchParams(next, { replace: true });
  };

  const setBrowseMode = (mode: BrowseMode) => {
    setBrowseModeState(mode);
    const next = new URLSearchParams(searchParams);
    next.set('mode', mode);
    setSearchParams(next, { replace: true });
  };

  const setSelectedCategory = (cat: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (cat) {
      next.set('category', cat);
      next.delete('authority');
      next.set('mode', 'CATEGORY');
    } else {
      next.delete('category');
    }
    setSearchParams(next);
  };

  const setSelectedAuthorityId = (authId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (authId) {
      next.set('authority', authId);
      next.delete('category');
      next.set('mode', 'AUTHORITY');
    } else {
      next.delete('authority');
    }
    setSearchParams(next);
  };

  // Group services by category dynamically from services array
  const categoriesData = useMemo(() => {
    const map = new Map<string, ServiceSummary[]>();

    // Ensure canonical categories exist in map
    for (const cat of SERVICE_CATEGORIES) {
      map.set(cat, []);
    }

    for (const s of services) {
      const list = map.get(s.category) ?? [];
      list.push(s);
      map.set(s.category, list);
    }

    return Array.from(map.entries()).map(([catKey, list]) => ({
      key: catKey,
      label: SERVICE_CATEGORY_LABELS[catKey as ServiceCategory] ?? catKey.replace(/_/g, ' '),
      image: CATEGORY_IMAGES[catKey] ?? null,
      services: list,
      count: list.length,
    }));
  }, [services]);

  // Group services by authority dynamically from services array (ordered by creation order to match Admin panel)
  const authoritiesData = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string | null;
        name: string;
        code: string | null;
        image: string | null;
        createdAt: string | Date | null;
        services: ServiceSummary[];
      }
    >();

    for (const s of services) {
      const authKey = s.authority ? s.authority.id : 'DIRECT_SERVICES';
      const authName = s.authority ? s.authority.name : 'Direct Services';
      const authCode = s.authority?.code ?? null;
      const authImage = s.authority?.images?.[0] ?? null;
      const authCreatedAt = s.authority?.createdAt ?? null;

      const existing = map.get(authKey) ?? {
        id: s.authority?.id ?? null,
        name: authName,
        code: authCode,
        image: authImage,
        createdAt: authCreatedAt,
        services: [],
      };
      if (!existing.image && authImage) {
        existing.image = authImage;
      }
      if (!existing.createdAt && authCreatedAt) {
        existing.createdAt = authCreatedAt;
      }
      existing.services.push(s);
      map.set(authKey, existing);
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.id === null) return 1;
      if (b.id === null) return -1;
      if (a.createdAt && b.createdAt) {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      return 0;
    });
  }, [services]);

  // Filtered services for Search mode
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return services.filter((s) => {
      const nameMatch = s.name.toLowerCase().includes(q);
      const descMatch = (s.description ?? '').toLowerCase().includes(q);
      const catMatch = (SERVICE_CATEGORY_LABELS[s.category] ?? s.category)
        ?.toLowerCase()
        .includes(q);
      const authNameMatch = s.authority?.name.toLowerCase().includes(q);
      const authCodeMatch = s.authority?.code?.toLowerCase().includes(q);
      return nameMatch || descMatch || catMatch || authNameMatch || authCodeMatch;
    });
  }, [services, query]);

  // Services matching selected Category
  const selectedCategoryServices = useMemo(() => {
    if (!selectedCategory) return [];
    return services.filter((s) => s.category === selectedCategory);
  }, [services, selectedCategory]);

  // Services matching selected Authority
  const selectedAuthorityData = useMemo(() => {
    if (!selectedAuthorityId) return null;
    return authoritiesData.find(
      (a) => (a.id === null ? 'DIRECT_SERVICES' : a.id) === selectedAuthorityId,
    );
  }, [authoritiesData, selectedAuthorityId]);

  const isSearchActive = query.trim().length > 0;
  const isDrillDownActive =
    !isSearchActive && (selectedCategory !== null || selectedAuthorityId !== null);

  const handleClearSelection = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('category');
    next.delete('authority');
    setSearchParams(next);
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
      {/* Page Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-2xl">Hello, {firstName}!</h1>
          <p className="mt-1 text-base text-gray-600 sm:text-sm">
            What can we help you with today?
          </p>
        </div>
        <div className="relative w-full sm:max-w-sm">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search service, category or authority..."
            className="pl-9 text-base md:text-sm"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400 hover:text-gray-600"
            >
              Clear
            </button>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <section className="mt-6 space-y-6 sm:mt-8">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500">
            <svg
              className="h-8 w-8 animate-spin text-brand-600 mb-3"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-sm font-medium">Loading catalog services...</p>
          </div>
        ) : isSearchActive ? (
          /* Search Results View */
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">
                Search Results ({searchResults.length})
              </h2>
              <button
                onClick={() => setQuery('')}
                className="text-xs font-semibold text-brand-700 hover:underline"
              >
                Clear search
              </button>
            </div>
            {searchResults.length === 0 ? (
              <EmptyState query={query} />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {searchResults.map((s) => (
                  <ServiceCard key={s.id} service={s} />
                ))}
              </div>
            )}
          </div>
        ) : isDrillDownActive ? (
          /* Category or Authority Drill-down View */
          <div>
            {/* Back Navigation Bar (No Category/Authority Banner as requested) */}
            <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
              <button
                type="button"
                onClick={handleClearSelection}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-1.5 text-xs font-bold text-gray-700 shadow-sm transition-all hover:bg-brand-50 hover:text-brand-800 hover:border-brand-300 active:scale-95 cursor-pointer"
              >
                <svg
                  className="h-4 w-4 text-brand-700"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                  />
                </svg>
                <span>Back to {selectedCategory ? 'Categories' : 'Authorities'}</span>
              </button>

              <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-extrabold text-brand-800 border border-brand-200">
                {selectedCategory
                  ? `${selectedCategoryServices.length} Services`
                  : `${selectedAuthorityData?.services.length ?? 0} Services`}
              </span>
            </div>

            {/* Direct List of Services or Empty State */}
            {(selectedCategory ? selectedCategoryServices : (selectedAuthorityData?.services ?? []))
              .length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 p-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 mb-3 border border-emerald-100">
                  <svg
                    className="h-8 w-8 text-emerald-700"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-gray-900">No services available</h3>
                <p className="mt-1 max-w-sm text-sm text-gray-500">
                  There are currently no services listed under this{' '}
                  {selectedCategory ? 'category' : 'authority'}.
                </p>
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-brand-700 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-brand-800 transition-colors cursor-pointer"
                >
                  Browse all {selectedCategory ? 'categories' : 'authorities'}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {(selectedCategory
                  ? selectedCategoryServices
                  : (selectedAuthorityData?.services ?? [])
                ).map((s) => (
                  <ServiceCard key={s.id} service={s} />
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Cards Selection Overview View (Default) */
          <div>
            {/* Mode Switcher Tabs */}
            <div className="mb-5 flex items-center justify-between border-b border-gray-200 pb-3">
              <div className="flex items-center gap-1.5 rounded-xl bg-gray-100 p-1">
                <button
                  onClick={() => setBrowseMode('AUTHORITY')}
                  className={cn(
                    'rounded-lg px-3.5 py-1.5 text-xs font-extrabold transition-all duration-150',
                    browseMode === 'AUTHORITY'
                      ? 'bg-brand-700 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900',
                  )}
                >
                  By Authority ({authoritiesData.length})
                </button>
                <button
                  onClick={() => setBrowseMode('CATEGORY')}
                  className={cn(
                    'rounded-lg px-3.5 py-1.5 text-xs font-extrabold transition-all duration-150',
                    browseMode === 'CATEGORY'
                      ? 'bg-brand-700 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900',
                  )}
                >
                  By Category
                </button>
              </div>

              <span className="hidden text-xs font-semibold text-gray-500 sm:inline-block">
                Tap card to view services
              </span>
            </div>

            {/* Authority vs Category Grid (Both preserved in DOM for instant smooth tab switching) */}
            <div
              className={cn(
                'grid grid-cols-2 gap-2.5 sm:gap-4 md:grid-cols-2',
                browseMode !== 'AUTHORITY' && 'hidden',
              )}
            >
              {authoritiesData.map((authItem) => {
                const authKey = authItem.id === null ? 'DIRECT_SERVICES' : authItem.id;
                return (
                  <AuthorityCard
                    key={authKey}
                    name={authItem.name}
                    code={authItem.code}
                    image={authItem.image}
                    count={authItem.services.length}
                    onClick={() => setSelectedAuthorityId(authKey)}
                  />
                );
              })}
            </div>

            <div
              className={cn(
                'grid grid-cols-2 gap-2.5 sm:gap-4 md:grid-cols-2',
                browseMode !== 'CATEGORY' && 'hidden',
              )}
            >
              {categoriesData.map((catItem) => (
                <CategoryCard
                  key={catItem.key}
                  label={catItem.label}
                  image={catItem.image}
                  count={catItem.count}
                  onClick={() => setSelectedCategory(catItem.key)}
                />
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

{
  /* Category Card matching Authority Card design */
}
export function CategoryCard({
  label,
  code,
  image,
  count,
  onClick,
}: {
  label: string;
  code?: string | null;
  image?: string | null;
  count: number;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className="group cursor-pointer rounded-2xl border border-emerald-100/80 bg-white p-3 sm:p-5 shadow-sm hover:border-emerald-300 hover:shadow-md transition-all duration-200 active:scale-[0.99] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-4 select-none min-h-[145px] sm:min-h-0"
    >
      {/* Top row on mobile / Left on desktop */}
      <div className="flex items-center justify-between sm:justify-start gap-2">
        <div className="flex h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl bg-[#effaf4] p-1 sm:p-2 border border-emerald-100/60 overflow-hidden">
          {image ? (
            <img
              src={image}
              alt={label}
              loading="eager"
              decoding="async"
              className="h-full w-full object-contain rounded-lg sm:rounded-xl"
            />
          ) : (
            <svg
              className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-700 opacity-80"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          )}
        </div>

        {/* Code badge shown at top right on mobile */}
        {code ? (
          <span className="sm:hidden rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-black tracking-wider text-emerald-800 border border-emerald-200 uppercase">
            {code}
          </span>
        ) : null}
      </div>

      {/* Title & Service count (Middle on desktop, Center on mobile) */}
      <div className="flex flex-col justify-between min-w-0 flex-1 py-0.5">
        <h3 className="text-xs sm:text-base font-extrabold text-gray-900 line-clamp-2 leading-tight sm:leading-snug">
          {label}
        </h3>

        <div className="mt-2 sm:mt-3 hidden sm:block">
          <span className="inline-flex items-center rounded-xl bg-emerald-50 px-3.5 py-1 text-xs sm:text-sm font-extrabold text-emerald-800 border border-emerald-200/80">
            {count} {count === 1 ? 'Service Available' : 'Services Available'}
          </span>
        </div>
      </div>

      {/* Bottom row on mobile / Right column on desktop */}
      <div className="flex sm:flex-col items-center sm:items-end justify-between self-stretch shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-gray-100">
        {/* Code badge on desktop */}
        {code ? (
          <span className="hidden sm:inline-block rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-black tracking-wider text-emerald-800 border border-emerald-200 uppercase">
            {code}
          </span>
        ) : (
          <div className="hidden sm:block" />
        )}

        {/* Service count badge on mobile */}
        <span className="sm:hidden inline-flex items-center rounded-lg bg-emerald-50 px-2 py-0.5 text-[10px] font-extrabold text-emerald-800 border border-emerald-200/80">
          {count} {count === 1 ? 'Service' : 'Services'}
        </span>

        {/* Static Arrow Button (NO HOVER COLOUR CHANGE ON ARROW) */}
        <div className="flex h-8 w-8 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50/80 text-emerald-700 shadow-2xs border border-emerald-200/60">
          <svg
            className="h-4 w-4 sm:h-5 sm:w-5 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
          >
            <path d="M5 12h14m-7-7 7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}

{
  /* Authority Card with 2-cards-per-line mobile layout and static arrow */
}
export function AuthorityCard({
  name,
  code,
  image,
  count,
  onClick,
}: {
  name: string;
  code: string | null;
  image?: string | null;
  count: number;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className="group cursor-pointer rounded-2xl border border-emerald-100/80 bg-white p-3 sm:p-5 shadow-sm hover:border-emerald-300 hover:shadow-md transition-all duration-200 active:scale-[0.99] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-4 select-none min-h-[145px] sm:min-h-0"
    >
      {/* Top row on mobile / Left on desktop */}
      <div className="flex items-center justify-between sm:justify-start gap-2">
        <div className="flex h-16 w-16 sm:h-20 sm:w-20 lg:h-24 lg:w-24 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl bg-[#effaf4] p-1 sm:p-2 border border-emerald-100/60 overflow-hidden">
          {image ? (
            <img
              src={image}
              alt={name}
              loading="eager"
              decoding="async"
              className="h-full w-full object-contain rounded-lg sm:rounded-xl"
            />
          ) : (
            <svg
              className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-700 opacity-80"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m0 0h4m-4 0V11m0 0V7m0 4h4m-4 0H7"
              />
            </svg>
          )}
        </div>

        {/* Code badge shown at top right on mobile */}
        {code ? (
          <span className="sm:hidden rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-black tracking-wider text-emerald-800 border border-emerald-200 uppercase">
            {code}
          </span>
        ) : null}
      </div>

      {/* Title & Service count (Middle on desktop, Center on mobile) */}
      <div className="flex flex-col justify-between min-w-0 flex-1 py-0.5">
        <h3 className="text-xs sm:text-base font-extrabold text-gray-900 line-clamp-2 leading-tight sm:leading-snug">
          {name}
        </h3>

        <div className="mt-2 sm:mt-3 hidden sm:block">
          <span className="inline-flex items-center rounded-xl bg-emerald-50 px-3.5 py-1 text-xs sm:text-sm font-extrabold text-emerald-800 border border-emerald-200/80">
            {count} {count === 1 ? 'Service Available' : 'Services Available'}
          </span>
        </div>
      </div>

      {/* Bottom row on mobile / Right column on desktop */}
      <div className="flex sm:flex-col items-center sm:items-end justify-between self-stretch shrink-0 pt-1 sm:pt-0 border-t sm:border-t-0 border-gray-100">
        {/* Code badge on desktop */}
        {code ? (
          <span className="hidden sm:inline-block rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-black tracking-wider text-emerald-800 border border-emerald-200 uppercase">
            {code}
          </span>
        ) : (
          <div className="hidden sm:block" />
        )}

        {/* Service count badge on mobile */}
        <span className="sm:hidden inline-flex items-center rounded-lg bg-emerald-50 px-2 py-0.5 text-[10px] font-extrabold text-emerald-800 border border-emerald-200/80">
          {count} {count === 1 ? 'Service' : 'Services'}
        </span>

        {/* Static Arrow Button (NO HOVER COLOUR CHANGE ON ARROW) */}
        <div className="flex h-8 w-8 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50/80 text-emerald-700 shadow-2xs border border-emerald-200/60">
          <svg
            className="h-4 w-4 sm:h-5 sm:w-5 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
          >
            <path d="M5 12h14m-7-7 7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
}

export function EmptyState({ query }: { query: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-brand-50">
        <SearchIcon className="h-10 w-10 text-brand-700" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-gray-900">
        No services match &ldquo;{query}&rdquo;
      </h3>
      <p className="mt-1 max-w-sm text-sm text-gray-600">
        Try a different keyword, category, or authority name to find what you need.
      </p>
    </div>
  );
}
